/**
 * /fiches/eleve/livret-plateau-a2 — Livret interactif mobile-first
 *
 * L'élève coche chaque compétence : ✅ Acquis / 🟠 À consolider / ⚪ Non vu.
 * Sauvegarde locale (localStorage). Barre de progression + filtres par niveau.
 * Accessible via QR ou onglet dédié BO élève / BO moniteur.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Printer, RotateCcw, Trophy, CheckCircle2, AlertCircle, Circle,
  ChevronDown, ChevronUp, Sparkles, HelpCircle, Wrench, AlertTriangle, Filter,
} from 'lucide-react';

const STORAGE_KEY = 'code2roues:livret:plateau-a2:v1';
const PRINT_URL = '/fiches/moto/livret-progression-plateau-a2';

// ─── Données des 19 compétences (source de vérité unique) ─────────────
const NIVEAUX = [
  {
    n: '0',
    color: '#a855f7',
    title: 'Théorie fondamentale',
    desc: 'Guide 2-roues — À maîtriser AVANT de monter.',
    items: [
      {
        id: '0.1', title: 'Le regard — la clé absolue du motard',
        pourquoi: 'Une moto va TOUJOURS où va le regard (loi psychomotrice). C\'est LE point qui différencie un motard sûr d\'un motard dangereux.',
        comment: 'Haut (au-dessus de l\'horizon), loin (10-15 s en avance), mobile (rétros toutes les 5-10 s). En virage : regarder la sortie du virage.',
        risque: 'Fixer la roue avant ou un plot = tu vas droit dedans. Regarder l\'extérieur du virage = tu élargis.',
      },
      {
        id: '0.2', title: 'Position — les 5 points d\'appui',
        pourquoi: 'Bonne position = équilibre stable + contrôle précis + moins de fatigue. Le motard fait corps avec sa moto.',
        comment: '1. Pieds sur repose-pieds · 2. Genoux serrés au réservoir · 3. Fessiers ancrés dans la selle · 4. Mains relâchées · 5. Regard haut.',
        risque: 'Bras tendus = pas de contrôle du guidon. Genoux ouverts = moto instable dans les manœuvres lentes.',
      },
      {
        id: '0.3', title: 'Anatomie mécanique — connaître sa moto',
        pourquoi: 'Comprendre ce que fait ta moto quand tu actionnes une commande, c\'est anticiper ses réactions.',
        comment: 'Moteur 4-temps (A2 = 35 kW max). Transmission : embrayage → boîte 5/6 vitesses → chaîne → roue arrière. Freins : disque avant (~70%) + arrière (~30%). Suspensions + pneus (2 timbres-poste au sol).',
        risque: 'Ignorer un bruit anormal, chaîne détendue ou pression basse = casse ou chute. Faire une VVR à chaque prise en main.',
      },
      {
        id: '0.4', title: 'Les 4 lois physiques essentielles',
        pourquoi: 'La moto obéit à des lois physiques. Les connaître te donne des super-pouvoirs (contre-braquage) et t\'évite des accidents.',
        comment: '1. Adhérence limitée (règle 100%). 2. Transfert de charge au freinage. 3. Contre-braquage (au-dessus de 20 km/h, on pousse le guidon du côté du virage). 4. Gyroscopie stabilise la moto.',
        risque: 'Freiner en virage = chute. Tirer sur le guidon à haute vitesse = la moto va tout droit. Trop lent = la moto tombe.',
      },
      {
        id: '0.5', title: 'EPI & sécurité passive',
        pourquoi: 'En moto, pas d\'habitacle. Ton corps est le seul amortisseur. Les EPI réduisent de 70% le risque de blessure grave.',
        comment: 'Casque E22 (jugulaire bouclée), blouson avec protections CE, gants CE homologués, pantalon renforcé, chaussures montantes.',
        risque: 'Casque non bouclé = envol. Baskets = fractures malléoles. Gants trop grands = perte de sensation.',
      },
    ],
  },
  {
    n: '1',
    color: '#8b5cf6',
    title: 'Fondamentaux — Prise en main',
    desc: 'Les bases avant d\'aborder les 6 manœuvres officielles.',
    items: [
      {
        id: '1.1', title: 'Équilibre & manipulation à l\'arrêt',
        pourquoi: 'Une moto pèse 180-200 kg. Savoir la tenir et la manipuler sans forcer.',
        comment: 'Corps collé au réservoir, dos droit, utilise ton bassin (pas tes bras). Béquille latérale dans le sens de la pente.',
        risque: 'Chute au rebéquillage. Toujours vérifier la béquille avant de descendre.',
      },
      {
        id: '1.2', title: 'Les 4 commandes moto',
        pourquoi: 'Maîtriser embrayage, accélérateur, sélecteur, freins sans hésiter.',
        comment: 'Repérage à l\'arrêt, moteur éteint. Main gauche = embrayage. Main droite = frein avant. Pied gauche = sélecteur. Pied droit = frein arrière.',
        risque: 'Confondre frein avant et embrayage = chute immédiate.',
      },
      {
        id: '1.3', title: 'Démarrage & arrêt moteur',
        pourquoi: 'Démarrer et éteindre sans caler ni faire hurler le moteur.',
        comment: 'Neutre → contact → coupe-circuit ON → démarreur. Pour arrêter : passer au neutre puis couper.',
        risque: 'Démarrer en 1ère sans embrayer = la moto bondit. Toujours vérifier le neutre.',
      },
      {
        id: '1.4', title: 'Point de patinage embrayage',
        pourquoi: 'Trouver le point où la moto commence à avancer. Clé de tous les démarrages en douceur.',
        comment: 'Moto en butée sur freins avant. Embrayer, puis relâcher très lentement en écoutant le moteur qui plonge.',
        risque: 'Relâcher trop vite = calage. Trop lentement = usure prématurée de l\'embrayage.',
      },
      {
        id: '1.5', title: 'Roulage 1ʳᵉ en ligne droite',
        pourquoi: 'Avancer seul(e), en équilibre. Première vraie sensation de conduite.',
        comment: 'Regard loin (10-15 m devant), pieds sur les repose-pieds, patinage doux, légère accélération.',
        risque: 'Fixer la roue avant = la moto zigzague. La moto va où va le regard.',
      },
    ],
  },
  {
    n: '2',
    color: '#6366f1',
    title: 'Les 6 manœuvres officielles',
    desc: 'Manœuvres présentes le jour de l\'épreuve (Sécurité Routière).',
    items: [
      {
        id: '2.1', title: 'M1 — Sans l\'aide du moteur',
        pourquoi: 'Démontrer que tu maîtrises le poids moteur éteint. Note A ou B, non éliminatoire.',
        comment: 'Débéquiller en A → pousser jusqu\'à B → s\'arrêter → reculer → rebéquiller en A. En marchant à côté.',
        risque: 'Faire tomber la moto en la reculant. Garder le corps côté haut de la pente.',
      },
      {
        id: '2.2', title: 'M2 — Allure réduite sans passager (chrono ≥ 16 s)',
        pourquoi: 'Prouver que tu maîtrises la moto à basse vitesse. Note A si chrono ≥ 16 s entre A et B.',
        comment: 'Regard loin (jamais sur les plots), patinage embrayage constant, frein arrière en régulation, genoux au réservoir.',
        risque: 'Fixer les plots = tu les touches. Chrono < 14 s = éliminatoire.',
      },
      {
        id: '2.3', title: 'M3 — Freinage d\'urgence (≥ 50 km/h)',
        pourquoi: 'Prouver que tu sais t\'arrêter vite et précisément. Radar au C6.',
        comment: 'Accélérer franchement jusqu\'à 50 km/h min. Frein avant progressif (70%) + arrière (30%). Arrêt avant C5 (sec) / C4 (mouillé).',
        risque: 'Bloquer la roue avant = chute. Freinage avant C6 ou vitesse insuffisante = éliminatoire.',
      },
      {
        id: '2.4', title: 'M4 — Allure réduite AVEC passager',
        pourquoi: 'Démontrer que tu peux rouler en sécurité avec un passager.',
        comment: 'Passager annoncé (fauteuil arrière). Partir doucement, boucle lente, arrêt précis pour déposer.',
        risque: 'Démarrer brusquement avec un passager = équilibre perdu.',
      },
      {
        id: '2.5', title: 'M5 — Slalom (≥ 40 km/h C7, ≥ 50 km/h C6)',
        pourquoi: 'Maîtriser enchaînement rapide de virages + accélération franche.',
        comment: 'Regard sur le plot suivant (pas celui d\'avant). Contre-braquage : appuyer sur le guidon du côté du virage.',
        risque: 'Freiner dans le slalom = chute. Vitesse insuffisante = éliminatoire.',
      },
      {
        id: '2.6', title: 'M6 — Évitement (50 km/h + freinage plots colorés)',
        pourquoi: 'Simuler l\'évitement d\'un obstacle + arrêt précis.',
        comment: 'Arriver à 50 km/h franchi. Contre-braquer sec. Remettre la moto droite. Freinage précis entre les plots.',
        risque: 'Se raidir sur les bras = la moto ne bascule pas. Évitement trop tardif = heurt.',
      },
    ],
  },
  {
    n: '3',
    color: '#4f46e5',
    title: 'Maîtrise pour l\'examen',
    desc: 'Enchaîner en 10 min et gérer le stress.',
    items: [
      {
        id: '3.1', title: 'Enchaînement des 6 manœuvres',
        pourquoi: 'L\'épreuve dure 10 min. Il faut enchaîner sans temps mort ni hésitation.',
        comment: 'Répétition du parcours complet plusieurs fois. Visualisation mentale entre les passages.',
        risque: 'Trou de mémoire au milieu du parcours = échec. Anticiper la suite à chaque instant.',
      },
      {
        id: '3.2', title: 'Gestion du stress',
        pourquoi: 'Le jour J, le stress fait perdre 30% de tes moyens. Il faut le désamorcer.',
        comment: 'Respirer profondément avant le départ. Se concentrer sur SES gestes, pas sur l\'examinateur.',
        risque: 'Blocage à la 1ʳᵉ manœuvre = chute d\'attention pour les suivantes.',
      },
      {
        id: '3.3', title: 'Examens blancs',
        pourquoi: 'Se mettre en conditions réelles pour identifier tes derniers points faibles.',
        comment: 'Simulation minimum 3 fois avec un moniteur qui joue l\'examinateur.',
        risque: 'Passer l\'examen sans avoir simulé = surprise le jour J.',
      },
    ],
  },
];

const ALL_ITEMS = NIVEAUX.flatMap((niv) => niv.items.map((i) => ({ ...i, niveau: niv.n })));

const STATUS = {
  none: { label: 'Non vu', color: '#94a3b8', bg: 'bg-white/[0.03]', border: 'border-white/10', icon: Circle },
  todo: { label: 'À consolider', color: '#f59e0b', bg: 'bg-amber-500/15', border: 'border-amber-500/50', icon: AlertCircle },
  ok:   { label: 'Acquis', color: '#10b981', bg: 'bg-emerald-500/15', border: 'border-emerald-500/50', icon: CheckCircle2 },
};

function loadState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch (_) { return {}; }
}
function saveState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export default function LivretInteractifPlateauA2() {
  const [state, setState] = useState({});
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all'); // all | 0 | 1 | 2 | 3 | todo
  const [showToast, setShowToast] = useState(null);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    document.title = 'Mon livret interactif · Plateau A2';
    setState(loadState());
  }, []);

  const setStatus = (id, next) => {
    setState((prev) => {
      const updated = { ...prev, [id]: next };
      saveState(updated);
      return updated;
    });
    setShowToast(next === 'ok' ? '✅ Acquis !' : next === 'todo' ? '🟠 À consolider' : '↺ Remis à zéro');
    setTimeout(() => setShowToast(null), 1600);
  };

  const resetAll = () => {
    setState({});
    saveState({});
    setShowReset(false);
    setShowToast('↺ Livret réinitialisé');
    setTimeout(() => setShowToast(null), 1600);
  };

  const stats = useMemo(() => {
    const total = ALL_ITEMS.length;
    let ok = 0, todo = 0;
    for (const it of ALL_ITEMS) {
      if (state[it.id] === 'ok') ok++;
      else if (state[it.id] === 'todo') todo++;
    }
    return { total, ok, todo, none: total - ok - todo, pct: Math.round((ok / total) * 100) };
  }, [state]);

  const filteredNiveaux = useMemo(() => {
    if (filter === 'all') return NIVEAUX;
    if (filter === 'todo') {
      return NIVEAUX.map((n) => ({ ...n, items: n.items.filter((i) => state[i.id] === 'todo') })).filter((n) => n.items.length > 0);
    }
    return NIVEAUX.filter((n) => n.n === filter);
  }, [filter, state]);

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24">
      {/* Header sticky avec progression */}
      <div className="sticky top-0 z-20 bg-neutral-950/95 backdrop-blur border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <Link
              to="/magazine"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.10] text-white/85 text-[12px] font-semibold transition"
              data-testid="btn-back"
              aria-label="Retour au Magazine Code2Roues"
            >
              <ArrowLeft size={14} /> Retour
            </Link>
            <div className="flex items-center gap-2">
              <Link
                to={PRINT_URL}
                target="_blank"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.10] text-white/80 text-[11px] font-bold transition"
                data-testid="btn-print-version"
              >
                <Printer size={12} /> Imprimable
              </Link>
              <button
                onClick={() => setShowReset(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-red-500/10 hover:border-red-500/30 text-white/80 text-[11px] font-bold transition"
                data-testid="btn-reset"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>
          </div>

          {/* Barre de progression */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${stats.pct}%`,
                  background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                }}
                data-testid="progress-bar"
              />
            </div>
            <div className="text-[13px] font-black text-white shrink-0" style={{ minWidth: 44, textAlign: 'right' }}>
              {stats.pct}%
            </div>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[10.5px]">
            <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
              <CheckCircle2 size={11} /> {stats.ok} acquis
            </span>
            <span className="inline-flex items-center gap-1 text-amber-400 font-bold">
              <AlertCircle size={11} /> {stats.todo} à consolider
            </span>
            <span className="inline-flex items-center gap-1 text-white/40 font-bold">
              <Circle size={11} /> {stats.none} non vu
            </span>
            <span className="ml-auto text-white/40">/ {stats.total}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-violet-600/25 via-violet-500/10 to-transparent p-5">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-violet-400 mb-1">
            <Sparkles size={11} /> Mon livret plateau
          </div>
          <h1 className="text-[22px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Plateau A2 — Livret interactif
          </h1>
          <p className="text-white/70 text-[12.5px] mt-1.5 leading-relaxed">
            Coche chaque compétence à ton rythme. Trois états possibles : <strong className="text-emerald-300">Acquis</strong>, <strong className="text-amber-300">À consolider</strong> ou <strong className="text-white/60">Non vu</strong>. Ta progression est sauvegardée automatiquement sur ce téléphone.
          </p>
          {stats.pct === 100 && (
            <div className="mt-3 flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/50 rounded-lg px-3 py-2 text-emerald-300 text-[12px] font-bold">
              <Trophy size={14} /> 100% ! Tu es prêt(e) pour l&apos;examen 🎉
            </div>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="max-w-2xl mx-auto px-4 mt-4">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="filters">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} icon={Filter} label="Tout" />
          <FilterChip active={filter === '0'} onClick={() => setFilter('0')} color="#a855f7" label="N0 · Théorie" />
          <FilterChip active={filter === '1'} onClick={() => setFilter('1')} color="#8b5cf6" label="N1 · Bases" />
          <FilterChip active={filter === '2'} onClick={() => setFilter('2')} color="#6366f1" label="N2 · 6 manœuvres" />
          <FilterChip active={filter === '3'} onClick={() => setFilter('3')} color="#4f46e5" label="N3 · Examen" />
          <FilterChip active={filter === 'todo'} onClick={() => setFilter('todo')} color="#f59e0b" label={`À revoir (${stats.todo})`} />
        </div>
      </div>

      {/* Liste des compétences groupées par niveau */}
      <div className="max-w-2xl mx-auto px-4 mt-4 space-y-5">
        {filteredNiveaux.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/60 text-[13px]">
            🎉 Aucune compétence à consolider dans ce filtre !
          </div>
        )}
        {filteredNiveaux.map((niv) => (
          <div key={niv.n}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white text-[12px] font-black shrink-0"
                style={{ background: niv.color }}
              >
                {niv.n}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-white text-[14px] font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Niveau {niv.n} — {niv.title}
                </div>
                <div className="text-white/50 text-[10.5px] italic leading-tight">{niv.desc}</div>
              </div>
            </div>

            <div className="space-y-2">
              {niv.items.map((item) => (
                <CompetenceCard
                  key={item.id}
                  item={item}
                  status={state[item.id]}
                  open={openId === item.id}
                  onToggle={() => setOpenId((c) => c === item.id ? null : item.id)}
                  onStatus={(s) => setStatus(item.id, s)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bandeau bas — encouragement */}
      <div className="max-w-2xl mx-auto px-4 mt-8">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-1">💡 Chaque motard progresse à son rythme</div>
          <p className="text-white/80 text-[12.5px] leading-relaxed">
            Il n&apos;y a pas de délai standard. L&apos;important est de <strong>valider</strong> chaque étape avec ton moniteur avant de passer à la suivante. Consulte ce livret <strong>avant chaque leçon</strong> pour te rappeler les points clés.
          </p>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white text-neutral-900 px-4 py-2.5 rounded-full shadow-2xl text-[13px] font-bold" data-testid="toast">
          {showToast}
        </div>
      )}

      {/* Confirm reset */}
      {showReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setShowReset(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-5 text-neutral-900" onClick={(e) => e.stopPropagation()}>
            <div className="text-[14px] font-black mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Réinitialiser le livret ?</div>
            <div className="text-[12px] text-neutral-600 mb-4">Toutes tes cases cochées seront perdues. Cette action est irréversible.</div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowReset(false)}
                className="flex-1 px-3 py-2 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-800 text-[12px] font-bold"
                data-testid="btn-reset-cancel"
              >
                Annuler
              </button>
              <button
                onClick={resetAll}
                className="flex-1 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[12px] font-bold"
                data-testid="btn-reset-confirm"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sous-composants ─────────────────────────────────────────────────

function FilterChip({ active, onClick, icon: Icon, label, color }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition shrink-0 ${
        active
          ? 'bg-white text-neutral-900 border border-white'
          : 'bg-white/[0.04] text-white/70 border border-white/10 hover:bg-white/[0.10]'
      }`}
      style={active && color ? { background: color, color: '#fff', borderColor: color } : {}}
      data-testid="filter-chip"
    >
      {Icon && <Icon size={11} />} {label}
    </button>
  );
}

function CompetenceCard({ item, status, open, onToggle, onStatus }) {
  const s = STATUS[status] || STATUS.none;
  const Icon = s.icon;
  return (
    <div className={`rounded-xl border ${s.border} ${s.bg} transition overflow-hidden`} data-testid="competence-card">
      {/* Header cliquable */}
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 p-3 text-left hover:bg-white/[0.03] transition"
        data-testid="btn-toggle-competence"
      >
        <Icon size={20} style={{ color: s.color }} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[9.5px] font-black uppercase tracking-widest text-white/40">{item.id}</span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${s.color}30`, color: s.color }}>
              {s.label}
            </span>
          </div>
          <div className="text-white text-[13.5px] font-bold leading-tight">{item.title}</div>
        </div>
        {open ? <ChevronUp size={16} className="text-white/40 shrink-0" /> : <ChevronDown size={16} className="text-white/40 shrink-0" />}
      </button>

      {/* Corps déroulant : Pourquoi / Comment / Risque */}
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-white/5 pt-3">
          <PCRBlock icon={HelpCircle} color="#0ea5e9" label="Pourquoi" text={item.pourquoi} />
          <PCRBlock icon={Wrench} color="#10b981" label="Comment" text={item.comment} />
          <PCRBlock icon={AlertTriangle} color="#f43f5e" label="Risque" text={item.risque} />

          {/* Boutons de statut */}
          <div className="grid grid-cols-3 gap-1.5 mt-3">
            <StatusBtn label="Non vu" color="#94a3b8" icon={Circle} active={!status || status === 'none'} onClick={() => onStatus('none')} testId="btn-none" />
            <StatusBtn label="À consolider" color="#f59e0b" icon={AlertCircle} active={status === 'todo'} onClick={() => onStatus('todo')} testId="btn-todo" />
            <StatusBtn label="Acquis" color="#10b981" icon={CheckCircle2} active={status === 'ok'} onClick={() => onStatus('ok')} testId="btn-ok" />
          </div>
        </div>
      )}
    </div>
  );
}

function PCRBlock({ icon: Icon, color, label, text }) {
  return (
    <div className="rounded-lg p-2.5 border" style={{ background: `${color}10`, borderColor: `${color}30` }}>
      <div className="flex items-center gap-1 mb-1">
        <Icon size={12} style={{ color }} />
        <span className="text-[9.5px] font-black uppercase tracking-widest" style={{ color }}>{label}</span>
      </div>
      <div className="text-white/80 text-[12px] leading-snug">{text}</div>
    </div>
  );
}

function StatusBtn({ label, color, icon: Icon, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[11px] font-bold transition ${
        active
          ? 'text-white shadow-md scale-[1.02]'
          : 'bg-white/[0.03] text-white/60 border border-white/10 hover:bg-white/[0.08]'
      }`}
      style={active ? { background: color } : {}}
      data-testid={testId}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
